import { Container, Text, Title, Link as LinkComponent } from '@firsttable/web-components/atoms';
import { ErrorBoundary as ReactErrorBoundary } from 'react-error-boundary';
import React, { memo } from 'react';
import Box from '@firsttable/web-components/atoms/Box/Box';
import Link from 'next/link';
import { LinkConvertorType } from '@/components/analytics/hocs/withTrackableNavLinkComponent';
import { reportErrorToNewRelic } from '@/lib/newrelic/reportError';

const onErrorHandler = (
  error: Error,
  info?: {
    componentStack: string;
  }
) => {
  reportErrorToNewRelic(
    error,
    info?.componentStack ? { componentStack: info.componentStack } : undefined
  );
};

const NavLinkComponent = memo(({ href, children, onClick, ...props }: LinkConvertorType) => {
  let slug = href;
  if (slug === undefined) {
    slug = '/404/'; // @todo something is spitting out no link values which throws error
  }
  return (
    <Link href={slug} {...props} passHref legacyBehavior onClick={onClick}>
      {children}
    </Link>
  );
});

NavLinkComponent.displayName = 'LinkConvertor';

// This is the default error fallback component. You can override it by passing in your own ErrorFallback component
const DefaultErrorFallback = ({ error }: { error?: Error }) => (
  <Container py={[20, 60]}>
    <Title as="h1">Something went wrong</Title>
    <Text>We&lsquo;ve been alerted, and working on getting things back to normal again.</Text>
    <Box as="pre">{error?.message}</Box>
    <Text>
      Would you like to{' '}
      <LinkComponent href="/contact-us" NavComponent={NavLinkComponent} underline color="brand.red">
        contact us
      </LinkComponent>
      ?
    </Text>
  </Container>
);

// Use this component to render errors e.g. Apollo errors: Example: if (error) return <ErrorHandler error={error} />; It calls onErrorHandler to log the error to New Relic
const ErrorHandler = ({
  error,
  ErrorFallback = DefaultErrorFallback,
}: {
  error?: Error;
  ErrorFallback?: React.FC<{ error?: Error }>;
}) => {
  if (error) {
    onErrorHandler(error);
  }

  return <ErrorFallback error={error} />;
};

// Use this component to wrap areas that might throw errors
const ErrorBoundary = ({
  children,
  ErrorFallback = DefaultErrorFallback,
}: {
  children?: React.ReactNode;
  ErrorFallback?: React.FC<{ error?: Error }>;
}) => (
  <ReactErrorBoundary FallbackComponent={ErrorFallback} onError={onErrorHandler}>
    {children}
  </ReactErrorBoundary>
);

export default ErrorBoundary;

export { ErrorHandler };
